import axios from 'axios';
import env, { isProd } from '@utils/env';
import { getFromStorage, removeFromStorage } from '@utils/storageUtils';

import {
  CONTENTA_AUTH0_ACCESS_TOKEN,
  CONTENTA_B2B_ORGANIZATION_ID,
  CONTENTA_TOKEN,
} from 'constants/storages-values';

const api = axios.create({ baseURL: env.API });
export const publicApi = axios.create({ baseURL: env.API });

const PUBLIC_URLS = [
  '/public',
  'highlights',
  '/oauth/public',
  '/travelpartners',
];

/**
 * Endpoints that are public for one verb only. `/organization` is the clear
 * case: POST is the permitAll corporate signup, while GET is the admin listing
 * and must keep its token. Matching on the path alone would strip it.
 */
const PUBLIC_ENDPOINTS = [{ method: 'post', pathname: '/organization' }];

/**
 * Callers reach this interceptor with either a relative path (`api.post('/x')`)
 * or an absolute one (HttpClient prefixes the base URL), and the response
 * interceptor can hand over `undefined` when a request fails before config is
 * built. Normalising to a pathname keeps the exact matches above meaningful in
 * all three cases.
 */
const toPathname = (url) => {
  if (!url) return '';
  if (!url.includes('://')) return url.split('?')[0];

  try {
    return new URL(url).pathname;
  } catch {
    return url.split('?')[0];
  }
};

const shouldSkipAuthorization = (url, method) => {
  const pathname = toPathname(url);

  if (pathname.includes('/public') || PUBLIC_URLS.includes(pathname)) {
    return true;
  }

  const verb = String(method || 'get').toLowerCase();

  return PUBLIC_ENDPOINTS.some(
    (endpoint) => endpoint.pathname === pathname && endpoint.method === verb
  );
};

const getToken = () => getFromStorage(CONTENTA_TOKEN);

api.interceptors.request.use((config) => {
  if (shouldSkipAuthorization(config.url, config.method)) return config;

  const tokenToUse = getToken();

  if (tokenToUse) config.headers.Authorization = `Bearer ${tokenToUse}`;

  return config;
});

const B2B_PATH_PREFIXES = ['/b2b/', '/travelpartners'];

const isB2BRoute = () =>
  B2B_PATH_PREFIXES.some((prefix) =>
    window.location.pathname.startsWith(prefix)
  );

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url;
    const isPublicCall = shouldSkipAuthorization(url, error.config?.method);

    if (!isProd) {
      console.log(
        '[api.response error]',
        status,
        url,
        '| isB2BRoute:',
        isB2BRoute(),
        '| skipAuth:',
        isPublicCall
      );
    }

    if (status === 401 && isProd && !isPublicCall) {
      removeFromStorage(CONTENTA_TOKEN);
      removeFromStorage(CONTENTA_AUTH0_ACCESS_TOKEN);
      removeFromStorage(CONTENTA_B2B_ORGANIZATION_ID);
      window.location.href = isB2BRoute() ? '/travelpartners/login' : '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
